import { useEffect, useRef, useState } from 'react';
import { lockScroll } from '../lib/scroll-lock';
import { createPortal } from 'react-dom';
import { User, X } from 'lucide-react';

// Bios are often pasted from documents with hard line breaks mid-sentence.
// Keep blank-line paragraph breaks; let everything else reflow to the width.
export const bioParagraphs = (text) => String(text || '')
  .split(/\n\s*\n/)
  .map((para) => para.replace(/\s*\n\s*/g, ' ').trim())
  .filter(Boolean);

// Pill button that opens a presenter's bio. Matches the old inline "Bio" toggle.
export function BioButton({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-haspopup="dialog"
      className="inline-flex items-center gap-0.5 text-[10px] font-bold text-brand hover:text-brand-dark transition-colors px-2 py-0.5 rounded-full border border-brand/30 hover:border-brand/50 hover:bg-brand/5"
    >
      Bio
    </button>
  );
}

// Presenter bio in a modal, with room for a large photo. Portaled to <body>
// because it can open from inside other modals (e.g. the homepage session
// modal), whose transforms would otherwise trap position: fixed.
export default function BioModal({ name, title, imageUrl, bio, onClose }) {
  const [closing, setClosing] = useState(false);
  const closingRef = useRef(false);

  const requestClose = () => {
    if (closingRef.current) return;
    closingRef.current = true;
    setClosing(true);
    setTimeout(onClose, 320);
  };

  useEffect(() => {
    // Capture phase + stopPropagation so Escape closes only this modal, not a
    // parent modal that also listens on document.
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      requestClose();
    };
    document.addEventListener('keydown', onKey, true);
    const unlock = lockScroll();
    return () => {
      document.removeEventListener('keydown', onKey, true);
      unlock();
    };
  }, []);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`About ${name}`}
      onClick={requestClose}
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/60"
      style={{
        animation: closing
          ? 'carouselFadeOut 0.3s ease-in forwards'
          : 'carouselFade 0.25s ease-out both',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 sm:p-8"
        style={{
          animation: closing
            ? 'modalSlideDown 0.3s cubic-bezier(0.32, 0, 0.67, 0) forwards'
            : 'fadeUp 0.3s cubic-bezier(0.22, 1, 0.36, 1) both',
        }}
      >
        <button
          type="button"
          onClick={requestClose}
          aria-label="Close"
          className="absolute top-4 right-4 w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-500 hover:text-slate-900 transition"
        >
          <X size={18} />
        </button>

        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5 sm:gap-7">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={name}
              className="w-40 h-40 sm:w-48 sm:h-48 rounded-2xl object-cover border border-slate-200 shrink-0"
            />
          ) : (
            <div className="w-28 h-28 rounded-2xl bg-brand/15 flex items-center justify-center text-brand shrink-0">
              <User size={44} />
            </div>
          )}
          <div className="min-w-0 flex-1 text-center sm:text-left">
            <div className="text-xs text-slate-500 uppercase tracking-wide font-medium">Presenter</div>
            <h2 className="text-2xl font-extrabold text-slate-900 px-6 sm:px-0 sm:pr-10">{name}</h2>
            {title && <p className="text-sm font-medium text-slate-600 mt-0.5">{title}</p>}
            <div className="mt-4 space-y-3 text-sm text-slate-600 leading-relaxed text-left">
              {bioParagraphs(bio).map((para, i) => <p key={i}>{para}</p>)}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
